<template>
  <div style="padding: 30px">
    <div class="d-flex justify-space-between align-center mb-4">
      <h2 class="text-h5">Locations</h2>
      <v-btn color="primary" @click="fetchFromApi" :loading="fetching" class="text-capitalize">
        <v-icon left>{{ icons.mdiDownload }}</v-icon>
        Fetch from Shopify API
      </v-btn>
    </div>

    <v-alert v-if="allLocationsEnabled" type="info" class="mb-4" outlined>
      All active locations are enabled for fulfillment
    </v-alert>

    <v-data-table :headers="headers" :items="locations" :items-per-page="10" class="elevation-1 mt-5">
      <template v-slot:top>
        <v-row style="padding: 10px">
          <v-col md="2" cols="6">
            <strong>Total: </strong> {{ totalLocationsCount }}
          </v-col>
          <v-col md="3" cols="6">
            <strong>Enabled: </strong> {{ enabledLocationsCount }}
          </v-col>
        </v-row>
      </template>
      <template v-slot:body="{ items }">
        <tbody v-if="!is_loading">
          <tr v-if="!items.length">
            <td colspan="8" class="text-center" style="padding: 20px 0px">
              <img :src="require('@/assets/images/misc/no-data.png')" alt="" width="250px" />
              <p style="font-size: 25px">No Data Found!!</p>
            </td>
          </tr>
          <tr v-for="item in items" :key="item.location_id">
            <td>{{ item.location_id }}</td>
            <td>
              <v-icon left color="primary">{{ icons.mdiStore }}</v-icon>
              {{ item.name }}
              <v-chip small class="ma-2" color="primary" v-if="item.is_default == 1" text-color="white">
                <v-icon small left>{{ icons.mdiStar }}</v-icon>
                Default
              </v-chip>
            </td>
            <td>
              <v-chip small :color="item.active == 1 ? 'success' : 'error'" text-color="white">
                {{ item.active == 1 ? 'Active' : 'Inactive' }}
              </v-chip>
            </td>
            <td>
              <v-chip
                small
                :color="isLocationEnabled(item) ? 'success' : 'grey'"
                text-color="white"
              >
                {{ isLocationEnabled(item) ? 'Enabled' : 'Disabled' }}
              </v-chip>
            </td>
            <td>
              <v-chip
                small
                :color="item.is_enabled_for_fulfillment == 1 ? 'info' : 'warning'"
                text-color="white"
              >
                {{ item.is_enabled_for_fulfillment == 1 ? 'Yes' : 'No' }}
              </v-chip>
            </td>
            <td>
              <v-icon left color="secondary" small>{{ icons.mdiMapMarker }}</v-icon>
              {{ formatAddress(item) }}
            </td>
            <td>{{ item.city }}, {{ item.province }}</td>
            <td>{{ item.country_name }}</td>
          </tr>
        </tbody>

        <tbody v-else>
          <tr>
            <td colspan="8" class="text-center" style="padding: 80px 0px">
              <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import store from '@/store'
import {
  mdiDownload,
  mdiStore,
  mdiStar,
  mdiMapMarker,
  mdiInformation,
} from '@mdi/js'

export default {
  data() {
    return {
      shop: null,
      is_loading: false,
      fetching: false,
      locations: [],
      enabledLocations: [],
      allLocationsEnabled: false,
      totalLocationsCount: 0,
      enabledLocationsCount: 0,
      headers: [
        { text: 'Location ID', value: 'location_id' },
        { text: 'Name', value: 'name' },
        { text: 'Status', value: 'active' },
        { text: 'Enabled', value: 'enabled' },
        { text: 'Fulfillment', value: 'is_enabled_for_fulfillment' },
        { text: 'Address', value: 'address1' },
        { text: 'City/Province', value: 'city' },
        { text: 'Country', value: 'country_name' },
      ],
      icons: {
        mdiDownload: mdiDownload,
        mdiStore: mdiStore,
        mdiStar: mdiStar,
        mdiMapMarker: mdiMapMarker,
        mdiInformation: mdiInformation,
      },
    }
  },
  mounted() {
    this.shop = store.getters.loggedInShop
    this.fetchLocations()
  },
  methods: {
    async fetchLocations() {
      try {
        this.is_loading = true
        let response = await axios.get(`/admin/shops/${this.shop.id}/locations`)
        if (response.data.success) {
          this.locations = response.data.all_locations || []
          this.enabledLocations = response.data.enabled_locations || []
          this.allLocationsEnabled = response.data.all_locations_enabled || false
          this.totalLocationsCount = response.data.total_locations_count || 0
          this.enabledLocationsCount = response.data.enabled_locations_count || 0
        }
      } catch (e) {
        console.error('Error fetching locations:', e)
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch locations',
        })
      } finally {
        this.is_loading = false
      }
    },
    async fetchFromApi() {
      try {
        this.fetching = true
        let response = await axios.get(`/admin/shops/${this.shop.id}/locations/fetch`)
        if (response.data.success) {
          this.$swal.fire({
            icon: 'success',
            title: 'Success',
            text: response.data.message || 'Locations fetched successfully',
          })
          await this.fetchLocations()
        } else {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.message || 'Failed to fetch locations',
          })
        }
      } catch (e) {
        console.error('Error fetching from API:', e)
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch locations from Shopify API',
        })
      } finally {
        this.fetching = false
      }
    },
    isLocationEnabled(location) {
      if (this.allLocationsEnabled) {
        return location.active == 1
      }
      return this.enabledLocations.some(
        (enabled) => enabled.location_id === location.location_id
      )
    },
    formatAddress(item) {
      const parts = [item.address1, item.address2].filter(Boolean)
      return parts.join(', ') || 'N/A'
    },
  },
}
</script>


